import { MultipleChoiceExamDetail, MultipleChoiceExamQuestions } from '../../../../store/exam/types';
import { setExitExamModal, setMultipleChoiceExamStep } from '../../../../store/exam/slice';
import { CurrentQuestion, ExamPhase, ExamRequest, ExamResponse, ExamRouteState } from '../types';
import { RecordedChunk } from '../../../../utils/media/ChunkedRecorder';
import { useVideoCapture } from '../../../../hooks/useVideoCapture';
import { uploadFileThunk } from '../../../../store/general/thunks';
import { useDispatch, useSelector } from '../../../../store/store';
import { setNotification } from '../../../../store/general/slice';
import { selectUserId } from '../../../../store/auth/selectors';
import { useCountDown } from '../../../../hooks/useCountDown';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    proctoringThunk,
    setExamAnswersThunk,
    getMultipleChoiceExamQuestionThunk,
} from '../../../../store/exam/thunks';
import {
    NO_ANSWER,
    ERROR_MESSAGE,
    EXAM_SUMMARY,
    PROCTORING_TYPE,
    ALREADY_ANSWERED,
} from '../constants';
import { initialExamLanguage, toExamLanguages } from '../../constants';

/**
 * Drives a multiple choice exam: fetches one question at a time, keeps the per-question
 * countdown and the recorded stream in step with it, and reports the answers.
 */
export const useMultipleChoiceExam = () => {
    const dispatch = useDispatch();
    const { state } = useLocation();
    const userId = useSelector(selectUserId);
    const { counter, pause, restart } = useCountDown();
    const { examId, action, jobTitle, translateLanguages } = (state || {}) as ExamRouteState;
    const languages = toExamLanguages(translateLanguages);

    const [isLoading, setIsLoading] = useState(false);
    const [questionNumber, setQuestionNumber] = useState(0);
    const [canSkipQuestion, setCanSkipQuestion] = useState(false);
    const [phase, setPhase] = useState<ExamPhase>('PreExam');
    const [selectedOption, setSelectedOption] = useState(NO_ANSWER);
    const [language, setLanguage] = useState(() => initialExamLanguage(languages));
    const [exam, setExam] = useState<MultipleChoiceExamQuestions | null>(null);
    const [detail, setDetail] = useState<MultipleChoiceExamDetail | null>(null);

    const question: CurrentQuestion = {
        examId: exam?.id,
        number: questionNumber,
        text: exam?.question.question || '',
        options: exam?.question.choices || [],
        total: detail?.n_questions || 0,
        isLast: detail?.n_questions === questionNumber,
    };

    // The chunk callback runs outside React's render cycle, so the destination it needs
    // is mirrored here rather than read from a stale closure.
    const uploadPathRef = useRef<string | null>(null);

    const uploadChunk = async ({ file, fileName }: RecordedChunk) => {
        const path = uploadPathRef.current;
        if (!path) return;

        await dispatch(uploadFileThunk({ fileName: `${path}/${fileName}`, file }));
    };

    const {
        webcamRef,
        nextChunk,
        stopCapture,
        startCapture,
    } = useVideoCapture({
        prefix: action === 'resume' ? 'resume' : undefined,
        onChunk: uploadChunk,
    });

    const notifyError = () => {
        dispatch(setNotification([ERROR_MESSAGE, 'error']));
    };

    const fetchExam = <T,>(body: ExamRequest): Promise<ExamResponse<T>> =>
        dispatch(getMultipleChoiceExamQuestionThunk(body)).unwrap();

    /** Logs the moment a question is put in front of the applicant. */
    const trackQuestion = (id: string, number: number) => {
        dispatch(proctoringThunk({
            actionId: 0,
            examId: id,
            question: number,
            type: PROCTORING_TYPE,
        }));
    };

    useEffect(() => {
        if (!examId) return;

        fetchExam<MultipleChoiceExamDetail>({ pre: EXAM_SUMMARY, examId })
            .then(({ body }) => setDetail(body))
            .catch(notifyError);
    }, []);

    useEffect(() => {
        // Out of time — hand in whatever is selected and move on.
        counter === 0 && next();
    }, [counter]);

    useEffect(() => {
        uploadPathRef.current = exam ? `${userId}/${exam.job_id}/${exam.id}` : null;
    }, [exam, userId]);

    /** Reports the last answer and holds the applicant until the recording is uploaded. */
    const finishExam = async (id: string) => {
        try {
            await dispatch(setExamAnswersThunk({ exam_id: id, answer: selectedOption })).unwrap();

            setPhase('CollectingData');
            // Hold on the "collecting your data" screen until the last chunk is uploaded.
            await stopCapture();
            dispatch(setMultipleChoiceExamStep('ExamEnd'));
        } catch {
            notifyError();
        }
    };

    const startExam = async () => {
        if (!examId) return;
        setIsLoading(true);

        try {
            const { body, statusCode } = await fetchExam<MultipleChoiceExamQuestions>({
                pre: '1',
                action,
                examId,
                language,
            });

            if (statusCode === ALREADY_ANSWERED) {
                await finishExam(detail?.id || '');
                return;
            }

            if (statusCode !== 200) return;

            setExam(body);
            setQuestionNumber(body.questionNumber);
            startCapture();
            setPhase('Question');
            setIsLoading(false);
            restart(body.question?.solving_time);
            trackQuestion(body.id, 1);
        } catch {
            setIsLoading(false);
            notifyError();
        }
    };

    const next = () => {
        if (!detail || !examId || phase !== 'Question') return;
        setIsLoading(true);
        pause();

        if (question.isLast) {
            finishExam(detail.id);
            return;
        }

        const number = questionNumber + 1;

        fetchExam<MultipleChoiceExamQuestions>({
            examId,
            pre: `${number}`,
            answer: selectedOption,
            language,
        })
            .then(({ body }) => {
                setExam(body);
                setQuestionNumber(number);
                setCanSkipQuestion(false);
                setSelectedOption(NO_ANSWER);
                setIsLoading(false);
                restart(body.question?.solving_time);
                trackQuestion(body.id, number);

                // One file per question: close the current one and carry straight on,
                // so nothing is lost between questions.
                nextChunk();
            })
            .catch(() => {
                setIsLoading(false);
                notifyError();
            });
    };

    /** Lets the applicant move on without answering, after reporting a problem. */
    const skipQuestion = () => {
        setCanSkipQuestion(true);
    };

    const exitExam = () => {
        dispatch(setExitExamModal(true));
    };

    return {
        next,
        phase,
        detail,
        action,
        userId,
        counter,
        jobTitle,
        question,
        language,
        languages,
        exitExam,
        webcamRef,
        isLoading,
        startExam,
        skipQuestion,
        selectedOption,
        canSkipQuestion,
        setLanguage,
        setSelectedOption,
    };
};
